import React from 'react';
import { Badge } from '../types/badge';
import { BadgeType } from '../types/badge';
import { cn } from '../lib/utils';

interface BadgeDisplayProps {
  badges: Badge[];
  maxDisplay?: number;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const getBadgeTypeColor = (type: BadgeType): string => {
  const colors: Record<BadgeType, string> = {
    [BadgeType.ACTIVE_MEMBER]: 'bg-blue-100 text-blue-800 border-blue-200',
    [BadgeType.SUPER_ACTIVE]: 'bg-purple-100 text-purple-800 border-purple-200',
    [BadgeType.CONTRIBUTOR]: 'bg-green-100 text-green-800 border-green-200',
    [BadgeType.MASTER_CONTRIBUTOR]: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    [BadgeType.COMMENTATOR]: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    [BadgeType.TOP_ENGAGER]: 'bg-pink-100 text-pink-800 border-pink-200',
    [BadgeType.POSITIVE_VIBES]: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    [BadgeType.INSPIRING_AUTHOR]: 'bg-rose-100 text-rose-800 border-rose-200',
    [BadgeType.FRIENDLY]: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    [BadgeType.WELL_CONNECTED]: 'bg-teal-100 text-teal-800 border-teal-200',
    [BadgeType.COMMUNITY_BUILDER]: 'bg-orange-100 text-orange-800 border-orange-200',
    [BadgeType.NEWBIE]: 'bg-gray-100 text-gray-800 border-gray-200',
    [BadgeType.ONE_MONTH_MEMBER]: 'bg-slate-100 text-slate-800 border-slate-200',
    [BadgeType.THREE_MONTH_LOYAL]: 'bg-amber-100 text-amber-800 border-amber-200',
    [BadgeType.ONE_YEAR_VETERAN]: 'bg-red-100 text-red-800 border-red-200',
    [BadgeType.VERIFIED]: 'bg-blue-100 text-blue-800 border-blue-200',
    [BadgeType.TRENDSETTER]: 'bg-violet-100 text-violet-800 border-violet-200',
    [BadgeType.STAR_OF_THE_WEEK]: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    [BadgeType.PEACEKEEPER]: 'bg-green-100 text-green-800 border-green-200',
    [BadgeType.BUG_HUNTER]: 'bg-red-100 text-red-800 border-red-200'
  };
  return colors[type] || 'bg-gray-100 text-gray-800 border-gray-200';
};

const getSizeClasses = (size: 'sm' | 'md' | 'lg'): string => {
  const sizes = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-xs px-2 py-1',
    lg: 'text-sm px-2.5 py-1.5'
  };
  return sizes[size];
};

export const BadgeDisplay: React.FC<BadgeDisplayProps> = ({ 
  badges, 
  maxDisplay = 3,
  className,
  size = 'sm'
}) => {
  if (!badges || badges.length === 0) return null;

  const badgesToShow = badges.slice(0, maxDisplay);
  const remainingCount = badges.length - maxDisplay;

  return (
    <div className={cn("flex items-center gap-1 flex-wrap", className)}>
      {badgesToShow.map((badge) => (
        <div
          key={badge.id}
          className={cn(
            "inline-flex items-center gap-1 rounded-full border font-medium",
            getBadgeTypeColor(badge.type),
            getSizeClasses(size)
          )}
          title={badge.description || badge.name}
        >
          {badge.iconUrl ? (
            <img 
              src={badge.iconUrl} 
              alt={badge.name} 
              className="w-3 h-3 rounded-full"
            />
          ) : (
            <span className="text-xs">🏆</span>
          )}
          <span className="truncate max-w-[80px]">{badge.name}</span>
        </div>
      ))}
      {remainingCount > 0 && (
        <div
          className={cn(
            "inline-flex items-center rounded-full border bg-gray-100 text-gray-700 border-gray-200 font-medium",
            getSizeClasses(size)
          )}
          title={`Và ${remainingCount} badge khác`}
        >
          +{remainingCount}
        </div>
      )}
    </div>
  );
};


